import { darken, PaletteOptions } from '@mui/material'

declare module '@mui/material/styles' {
    interface PaletteColor {
        extraLight: string
        extraDark: string
    }
}

export const greys = {
    50: '#FCFDFF',
    100: '#F0F0F1',
    150: '#E6E6E8',
    200: '#DFDFE2',
    300: '#C0BFC5',
    400: '#A8A6B0',
    500: '#908C9B', // 500 and below do NOT pass WCAG AA contrast (assuming #FFF bg)
    600: '#767184', // 600 and above do pass WCAG AA contrast (assuming #FFF bg)
    700: '#5B5766', // 700 and above do pass WCAG AAA contrast (assuming #FFF bg)
    800: '#3E3A46',
    900: '#28252C',
}

declare module '@mui/material/styles' {
    interface SimplePaletteColorOptions {
        extraLight?: string
        light?: string
        main: string
        dark?: string
        extraDark?: string
        contrastText?: string
    }

    interface Palette {
        border: {
            subtle: string
            primary: string
            emphasis: string
        }
    }
}

const primary = {
    extraLight: '#EBF3FE',
    light: '#CBDCFD',
    main: '#3465C8',
    dark: '#123F97',
    extraDark: '#152D5E',
}

const success = {
    extraLight: '#E2F5D9',
    light: '#7ACA55',
    main: '#378613',
    dark: '#1D5105',
    extraDark: '#103102',
}

const error = {
    extraLight: '#FFE4E4',
    light: '#F7978F',
    main: '#C22C1E',
    dark: '#962217',
    extraDark: '#60100A',
}

const warning = {
    extraLight: '#FFE9D4',
    light: '#FFC960',
    main: '#E4740C',
    dark: '#AE5604',
    extraDark: '#773B03',
}

const info = {
    extraLight: greys[100],
    light: greys[300],
    main: greys[700],
    dark: greys[800],
    extraDark: greys[900],
}

const brand = {
    main: '#5B39F5',
    dark: '#251865',
    green: '#D7F6C5',
}

const icons = {
    turquoise: '#58C6CD',
    gold: '#F59330',
    mint: '#7DBE93',
    mauve: '#AC6292',
    violet: '#7F1CB2',
    eggplant: '#342383',
    fuschia: '#A4198D',
    aqua: '#338FDE',
    chartreuse: '#568E3C',
    vermillion: '#F85931',
    teal: '#008080',
}

export const palette = {
    primary,
    success,
    error,
    warning,
    info,
    brand,
    icons,

    text: {
        primary: greys[800],
        secondary: greys[700],
        light: '#FFFFFF',
    },

    // We don't have a 'secondary' color, but this maintains compat. with MUI
    secondary: {
        extraLight: greys[600],
        light: greys[700],
        main: greys[800],
        dark: greys[900],
        extraDark: greys[900],
    },

    divider: greys[200],

    border: {
        subtle: greys[100],
        primary: greys[200],
        emphasis: greys[300],
    },

    grey: {
        A100: greys[100],
        A200: greys[200],
        A400: greys[400],
        A700: greys[700],
        ...greys,
    },

    background: {
        default: '#FFFFFF',
        paper: '#FFFFFF',
        accent: '#F3F7FD',
    },

    action: {
        // increase from MUI default of 0.04
        hoverOpacity: 0.1,
        hover: greys[150],
    },

    common: {
        black: '#000000',
        white: '#FFFFFF',
    },

}

export const darkPalette: PaletteOptions = {
    mode: 'dark',
    ...palette,
    info: {
        extraLight: greys[700],
        light: greys[500],
        main: greys[100],
        dark: greys[100],
        extraDark: greys[100],
    },
    text: {
        ...palette.text,
        primary: '#FFFFFF',
        secondary: greys[400],
    },
    action: {
        ...palette.action,
        hover: darken(primary.extraDark, 0.5),
    },
}

export const avatarPalette = {
    blue: palette.primary.main,
    turquoise: palette.icons.turquoise,
    gold: palette.icons.gold,
    mauve: palette.icons.mauve,
    violet: palette.icons.violet,
    eggplant: palette.icons.eggplant,
    fuschia: palette.icons.fuschia,
    aqua: palette.icons.aqua,
    // Green-ish and red-ish colors intentionally not included
    // chartreuse: palette.icons.chartreuse,
    // mint: palette.icons.mint,
    // teal: palette.icons.teal,
    // vermillion: palette.icons.vermillion,
}
